const state = {
    supportWidgetOpen: false,
    supportView: 'list', // 'list', 'create', 'detail'
    supportSelectedTicket: null,
    supportTickets: {
        data: null,
        loading: false,
        page: 1,
        pages: 1,
        total: 0
    },
    supportTicketDetail: {
        data: null,
        loading: false,
        messages: [],
        messagesLoading: false
    },
    supportUnreadCount: 0,
    supportFilters: {
        status: null,
        priority: null,
        assignment: null,
        user: null
    },
    supportStats: {
        open: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        unassigned: 0
    }
}

const getters = {
    supportWidgetOpen: state => state.supportWidgetOpen,
    supportView: state => state.supportView,
    supportSelectedTicket: state => state.supportSelectedTicket,
    supportTickets: state => state.supportTickets,
    supportTicketDetail: state => state.supportTicketDetail,
    supportUnreadCount: state => state.supportUnreadCount,
    supportFilters: state => state.supportFilters,
    supportStats: state => state.supportStats
}

const mutations = {
    support_set_widget_open(state, value) {
        state.supportWidgetOpen = value;
    },
    support_set_view(state, view) {
        state.supportView = view;
    },
    support_set_selected_ticket(state, ticketId) {
        state.supportSelectedTicket = ticketId;
    },
    support_set_tickets_data(state, data) {
        state.supportTickets.data = data.tickets || [];
        state.supportTickets.page = data.page || 1;
        state.supportTickets.pages = data.pages || 1;
        state.supportTickets.total = data.total || 0;
    },
    support_add_ticket(state, ticket) {
        if(state.supportTickets.data === null) {
            state.supportTickets.data = [];
        }
        state.supportTickets.data.unshift(ticket);
        state.supportTickets.total = (state.supportTickets.total || 0) + 1;
    },
    support_update_ticket(state, ticket) {
        if(state.supportTickets.data === null || !ticket || !ticket._id) { 
            return; 
        }
        const index = state.supportTickets.data.findIndex(t => t && t._id === ticket._id);
        if(index !== -1) {
            // Update the ticket - create new array reference AND new ticket object to ensure Vue reactivity
            const updatedData = [...state.supportTickets.data];
            // Create a new ticket object to ensure Vue detects the change
            updatedData[index] = { ...ticket };
            state.supportTickets.data = updatedData;
        }
        // Update ticket detail if it's the selected ticket
        if(state.supportTicketDetail.data && state.supportTicketDetail.data._id === ticket._id) {
            state.supportTicketDetail.data = { ...ticket };
        }
    },
    support_sort_tickets_by_updated(state) {
        if(state.supportTickets.data === null || state.supportTickets.data.length === 0) { 
            return; 
        }
        // Sort tickets by updatedAt (most recent first)
        // Create a new array to ensure Vue reactivity
        const sorted = [...state.supportTickets.data].sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA;
        });
        state.supportTickets.data = sorted;
    },
    support_set_tickets_loading(state, value) {
        state.supportTickets.loading = value;
    },
    support_set_ticket_detail(state, ticket) {
        // Update ticket data without affecting messages array
        // Messages are stored separately in supportTicketDetail.messages
        state.supportTicketDetail.data = ticket;
    },
    support_set_ticket_messages(state, messages) {
        state.supportTicketDetail.messages = messages || [];
    },
    support_add_ticket_message(state, message) {
        console.log('[Support] support_add_ticket_message: Adding message', {
            messageId: message?._id,
            ticketId: message?.ticketId,
            currentMessagesLength: state.supportTicketDetail.messages?.length || 0
        });
        
        if(state.supportTicketDetail.messages === null) {
            state.supportTicketDetail.messages = [];
        }
        
        // Check if message already exists to prevent duplicates
        const messageExists = state.supportTicketDetail.messages.some(m => m._id === message._id);
        if(messageExists) {
            console.log('[Support] support_add_ticket_message: Message already exists, skipping');
            return;
        }
        
        // Create new array reference for Vue reactivity
        state.supportTicketDetail.messages = [...state.supportTicketDetail.messages, message];
        console.log('[Support] support_add_ticket_message: Message added', {
            newMessagesLength: state.supportTicketDetail.messages.length
        });
    },
    support_set_ticket_detail_loading(state, value) {
        state.supportTicketDetail.loading = value;
    },
    support_set_ticket_messages_loading(state, value) {
        state.supportTicketDetail.messagesLoading = value;
    },
    support_set_unread_count(state, count) {
        state.supportUnreadCount = count || 0;
    },
    support_increment_unread_count(state) {
        state.supportUnreadCount = (state.supportUnreadCount || 0) + 1;
    },
    support_reset_unread_count(state) {
        state.supportUnreadCount = 0;
    },
    support_set_filters(state, filters) {
        state.supportFilters = { ...state.supportFilters, ...filters };
    },
    support_set_stats(state, stats) {
        state.supportStats = stats || {};
    }
}

const actions = {
    supportSetWidgetOpen({ commit }, value) {
        commit('support_set_widget_open', value);
    },
    supportSetView({ commit }, view) {
        commit('support_set_view', view);
    },
    supportSetSelectedTicket({ commit }, ticketId) {
        commit('support_set_selected_ticket', ticketId);
    },
    // User actions
    supportCreateTicketSocket({ getters, commit, dispatch }, data) {
        if(getters.socketGeneral === null || getters.socketSendLoading !== null) { return; }
        commit('socket_set_send_loading', 'SupportCreateTicket');

        getters.socketGeneral.emit('createSupportTicket', data, (res) => {
            if(res.success === false) {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to create ticket.' });
            } else {
                commit('support_add_ticket', res.ticket);
                commit('support_set_view', 'detail');
                commit('support_set_selected_ticket', res.ticket._id);
                commit('support_set_ticket_detail', res.ticket);
                commit('support_set_ticket_messages', res.ticket.messages || []);
                dispatch('notificationShow', { type: 'success', message: 'Ticket created successfully!' });
            }

            commit('socket_set_send_loading', null);
        });
    },
    supportGetTicketsSocket({ getters, commit, dispatch }, data) {
        if(getters.socketGeneral === null || getters.supportTickets.loading !== false) { return; }
        commit('support_set_tickets_loading', true);

        getters.socketGeneral.emit('getSupportTickets', data || {}, (res) => {
            if(res.success === true) {
                commit('support_set_tickets_data', res);
            } else {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to load tickets.' });
            }

            commit('support_set_tickets_loading', false);
        });
    },
    supportGetTicketDetailSocket({ getters, commit, dispatch }, data) {
        if(getters.socketGeneral === null || getters.supportTicketDetail.loading !== false) { return; }
        commit('support_set_ticket_detail_loading', true);
        commit('support_set_ticket_messages_loading', true);

        // Backend should automatically join user to ticket:${ticketId} room when this is called
        getters.socketGeneral.emit('getSupportTicketDetail', data, (res) => {
            if(res.success === true) {
                commit('support_set_ticket_detail', res.ticket);
                commit('support_set_ticket_messages', res.ticket.messages || []);
                commit('support_set_selected_ticket', res.ticket._id);
                console.log('[Support] supportGetTicketDetailSocket: Ticket detail loaded, user should be in ticket room', {
                    ticketId: res.ticket._id
                });
            } else {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to load ticket details.' });
            }

            commit('support_set_ticket_detail_loading', false);
            commit('support_set_ticket_messages_loading', false);
        });
    },
    supportSendMessageSocket({ getters, commit, dispatch }, data) {
        if(getters.socketGeneral === null || getters.socketSendLoading !== null) { return; }
        commit('socket_set_send_loading', 'SupportMessage');

        // Set a timeout to clear loading state if callback doesn't fire
        const timeoutId = setTimeout(() => {
            commit('socket_set_send_loading', null);
        }, 10000); // 10 second timeout

        getters.socketGeneral.emit('sendSupportMessage', data, (res) => {
            clearTimeout(timeoutId);
            commit('socket_set_send_loading', null);
            
            if(!res || res.success === false) {
                dispatch('notificationShow', res?.error || { type: 'error', message: 'Failed to send message.' });
                return;
            }
            
            // Add message to detail view
            // Server returns ticket with messages array, extract the last message
            let messageToAdd = null;
            
            if(res.message) {
                // Direct message in response
                messageToAdd = res.message;
            } else if(res.ticket && res.ticket.messages && Array.isArray(res.ticket.messages) && res.ticket.messages.length > 0) {
                // Extract the last message from ticket.messages array
                const ticketMessages = res.ticket.messages;
                const lastMessage = ticketMessages[ticketMessages.length - 1];
                
                if(lastMessage) {
                    // Ensure message has ticketId
                    messageToAdd = {
                        ...lastMessage,
                        ticketId: res.ticket._id || data.ticketId
                    };
                }
            }
            
            // Add message to chat list if we found one
            if(messageToAdd) {
                // Check if message already exists to prevent duplicates
                const messageExists = getters.supportTicketDetail.messages?.some(m => m._id === messageToAdd._id);
                if(!messageExists) {
                    commit('support_add_ticket_message', messageToAdd);
                    console.log('[Support] supportSendMessageSocket: Added message to chat list', {
                        messageId: messageToAdd._id,
                        ticketId: messageToAdd.ticketId
                    });
                }
            }
            
            // Update ticket last message time and move to top of list
            if(getters.supportTicketDetail.data && res.ticket) {
                const updatedTicket = { 
                    ...getters.supportTicketDetail.data, 
                    updatedAt: res.ticket.lastMessageAt || res.ticket.updatedAt || new Date().toISOString()
                };
                commit('support_set_ticket_detail', updatedTicket);
                commit('support_update_ticket', updatedTicket);
            }
            
            // Update ticket in list and sort by updatedAt
            if(getters.supportTickets.data && Array.isArray(getters.supportTickets.data) && getters.supportTickets.data.length > 0 && res.ticket) {
                const ticketIndex = getters.supportTickets.data.findIndex(t => t && t._id === data.ticketId);
                if(ticketIndex !== -1) {
                    const existingTicket = getters.supportTickets.data[ticketIndex];
                    const ticket = { 
                        ...existingTicket, 
                        updatedAt: res.ticket.lastMessageAt || res.ticket.updatedAt || new Date().toISOString()
                    };
                    commit('support_update_ticket', ticket);
                    // Sort tickets by updatedAt (most recent first) - creates new array reference
                    commit('support_sort_tickets_by_updated');
                }
            }
        });
    },
    supportCloseTicketSocket({ getters, commit, dispatch }, data) {
        if(getters.socketGeneral === null || getters.socketSendLoading !== null) { return; }
        commit('socket_set_send_loading', 'SupportCloseTicket');

        getters.socketGeneral.emit('closeSupportTicket', data, (res) => {
            if(res.success === false) {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to close ticket.' });
            } else {
                commit('support_update_ticket', res.ticket);
                if(getters.supportTicketDetail.data && getters.supportTicketDetail.data._id === res.ticket._id) {
                    commit('support_set_ticket_detail', res.ticket);
                }
                dispatch('notificationShow', { type: 'success', message: 'Ticket closed successfully.' });
            }

            commit('socket_set_send_loading', null);
        });
    },
    // Admin actions
    adminGetSupportTicketsSocket({ getters, commit, dispatch }, data) {
        if(getters.socketAdmin === null || getters.supportTickets.loading !== false) { return; }
        commit('support_set_tickets_loading', true);

        getters.socketAdmin.emit('getSupportTickets', data || {}, (res) => {
            if(res.success === true) {
                commit('support_set_tickets_data', res);
                if(res.stats) {
                    commit('support_set_stats', res.stats);
                }
            } else {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to load tickets.' });
            }

            commit('support_set_tickets_loading', false);
        });
    },
    adminGetSupportTicketDetailSocket({ getters, commit, dispatch }, data) {
        if(getters.socketAdmin === null || getters.supportTicketDetail.loading !== false) { return; }
        commit('support_set_ticket_detail_loading', true);
        commit('support_set_ticket_messages_loading', true);

        // Backend should automatically join admin to ticket:${ticketId} room when this is called
        getters.socketAdmin.emit('getSupportTicketDetail', data, (res) => {
            if(res.success === true) {
                commit('support_set_ticket_detail', res.ticket);
                commit('support_set_ticket_messages', res.ticket.messages || []);
                commit('support_set_selected_ticket', res.ticket._id);
                console.log('[Support] adminGetSupportTicketDetailSocket: Ticket detail loaded, admin should be in ticket room', {
                    ticketId: res.ticket._id
                });
            } else {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to load ticket details.' });
            }

            commit('support_set_ticket_detail_loading', false);
            commit('support_set_ticket_messages_loading', false);
        });
    },
    adminUpdateTicketStatusSocket({ getters, commit, dispatch }, data) {
        if(getters.socketAdmin === null || getters.socketSendLoading !== null) { return; }
        commit('socket_set_send_loading', 'SupportUpdateStatus');

        getters.socketAdmin.emit('updateSupportTicketStatus', data, (res) => {
            if(res.success === false) {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to update ticket status.' });
            } else {
                commit('support_update_ticket', res.ticket);
                if(getters.supportTicketDetail.data && getters.supportTicketDetail.data._id === res.ticket._id) {
                    commit('support_set_ticket_detail', res.ticket);
                }
            }

            commit('socket_set_send_loading', null);
        });
    },
    adminAssignTicketSocket({ getters, commit, dispatch }, data) {
        if(getters.socketAdmin === null || getters.socketSendLoading !== null) { return; }
        commit('socket_set_send_loading', 'SupportAssignTicket');

        getters.socketAdmin.emit('assignSupportTicket', data, (res) => {
            if(res.success === false) {
                dispatch('notificationShow', res.error || { type: 'error', message: 'Failed to assign ticket.' });
            } else {
                commit('support_update_ticket', res.ticket);
                if(getters.supportTicketDetail.data && getters.supportTicketDetail.data._id === res.ticket._id) {
                    commit('support_set_ticket_detail', res.ticket);
                }
            }

            commit('socket_set_send_loading', null);
        });
    },
    adminSendSupportMessageSocket({ getters, commit, dispatch }, data) {
        console.log('[Support] adminSendSupportMessageSocket: Called', { 
            ticketId: data?.ticketId, 
            hasSocket: !!getters.socketAdmin,
            loading: getters.socketSendLoading
        });
        
        if(getters.socketAdmin === null || getters.socketSendLoading !== null) { 
            console.log('[Support] adminSendSupportMessageSocket: Early return', {
                socketNull: getters.socketAdmin === null,
                loading: getters.socketSendLoading
            });
            return; 
        }
        commit('socket_set_send_loading', 'SupportMessage');

        // Set a timeout to clear loading state if callback doesn't fire
        const timeoutId = setTimeout(() => {
            console.log('[Support] adminSendSupportMessageSocket: Timeout - clearing loading');
            commit('socket_set_send_loading', null);
        }, 10000); // 10 second timeout

        getters.socketAdmin.emit('sendSupportMessage', data, (res) => {
            clearTimeout(timeoutId);
            commit('socket_set_send_loading', null);
            
            console.log('[Support] adminSendSupportMessageSocket: Callback received', {
                success: res?.success,
                hasMessage: !!res?.message,
                messageId: res?.message?._id,
                ticketId: data.ticketId
            });
            
            if(!res || res.success === false) {
                dispatch('notificationShow', res?.error || { type: 'error', message: 'Failed to send message.' });
                return;
            }
            
            // Add message to detail view
            // Server returns ticket with messages array, extract the last message
            console.log('[Support] adminSendSupportMessageSocket: Response data', {
                hasMessage: !!res.message,
                hasTicket: !!res.ticket,
                ticketMessages: res.ticket?.messages?.length,
                responseKeys: Object.keys(res || {})
            });
            
            let messageToAdd = null;
            
            if(res.message) {
                // Direct message in response
                messageToAdd = res.message;
                console.log('[Support] adminSendSupportMessageSocket: Found direct message in response');
            } else if(res.ticket && res.ticket.messages && Array.isArray(res.ticket.messages) && res.ticket.messages.length > 0) {
                // Extract the last message from ticket.messages array
                const currentMessagesLength = getters.supportTicketDetail.messages?.length || 0;
                const ticketMessages = res.ticket.messages;
                const lastMessage = ticketMessages[ticketMessages.length - 1];
                
                console.log('[Support] adminSendSupportMessageSocket: Extracting message from ticket', {
                    ticketMessagesLength: ticketMessages.length,
                    currentMessagesLength: currentMessagesLength,
                    lastMessageId: lastMessage?._id,
                    lastMessageText: lastMessage?.message
                });
                
                if(lastMessage) {
                    // Ensure message has ticketId
                    messageToAdd = {
                        ...lastMessage,
                        ticketId: res.ticket._id || data.ticketId
                    };
                    console.log('[Support] adminSendSupportMessageSocket: Created message object', {
                        messageId: messageToAdd._id,
                        ticketId: messageToAdd.ticketId,
                        messageText: messageToAdd.message
                    });
                }
            }
            
            // Add message to chat list if we found one
            if(messageToAdd) {
                // Check if message already exists to prevent duplicates
                const messageExists = getters.supportTicketDetail.messages?.some(m => m._id === messageToAdd._id);
                if(!messageExists) {
                    commit('support_add_ticket_message', messageToAdd);
                    console.log('[Support] adminSendSupportMessageSocket: Added message to chat list');
                } else {
                    console.log('[Support] adminSendSupportMessageSocket: Message already exists, skipping');
                }
            } else {
                console.log('[Support] adminSendSupportMessageSocket: No message found in response, waiting for socket event');
            }
            
            // Update ticket last message time and move to top of list
            if(getters.supportTicketDetail.data && res.ticket) {
                const updatedTicket = { 
                    ...getters.supportTicketDetail.data, 
                    updatedAt: res.ticket.lastMessageAt || res.ticket.updatedAt || new Date().toISOString()
                };
                commit('support_set_ticket_detail', updatedTicket);
                commit('support_update_ticket', updatedTicket);
            }
            
            // Update ticket in list and sort by updatedAt
            if(getters.supportTickets.data && Array.isArray(getters.supportTickets.data) && getters.supportTickets.data.length > 0) {
                const ticketIndex = getters.supportTickets.data.findIndex(t => t && t._id === data.ticketId);
                if(ticketIndex !== -1) {
                    const existingTicket = getters.supportTickets.data[ticketIndex];
                    const ticket = { 
                        ...existingTicket, 
                        updatedAt: res.message?.createdAt || res.message?.updatedAt || new Date().toISOString()
                    };
                    commit('support_update_ticket', ticket);
                    // Sort tickets by updatedAt (most recent first) - creates new array reference
                    commit('support_sort_tickets_by_updated');
                }
            }
        });
    },
    // Socket event handlers
    supportSocketTicket({ getters, commit }, data) {
        if(!data || !data.ticket || !data.ticket._id) { 
            return; 
        }
        
        // Update ticket in the list
        commit('support_update_ticket', data.ticket);
        // Sort tickets by updatedAt (most recent first) - this creates new array reference
        commit('support_sort_tickets_by_updated');
        
        // If this is the current ticket, update detail view
        if(getters.supportSelectedTicket === data.ticket._id) {
            commit('support_set_ticket_detail', data.ticket);
        }
    },
    supportSocketTicketCreated({ getters, commit }) {
        // New ticket notification - refresh list or increment count
        if(getters.supportView === 'list') {
            // Refresh list if on list view
            // This will be handled by component
        }
    },
    supportSocketTicketMessage({ getters, commit }, data) {
        console.log('[Support] supportSocketTicketMessage: 🔔 Received SOCKET EVENT', { 
            hasData: !!data, 
            hasMessage: !!data?.message, 
            ticketId: data?.message?.ticketId,
            messageId: data?.message?._id,
            messageText: data?.message?.message?.substring(0, 50),
            senderId: data?.message?.sender?.user,
            dataKeys: data ? Object.keys(data) : [],
            messageKeys: data?.message ? Object.keys(data.message) : []
        });
        
        if(!data) {
            console.log('[Support] supportSocketTicketMessage: Early return - no data object');
            return;
        }
        
        if(!data.message) {
            console.log('[Support] supportSocketTicketMessage: Early return - no message property', { 
                dataStructure: data,
                hasTicket: !!data.ticket,
                ticketMessages: data.ticket?.messages?.length
            });
            // Maybe the message is nested differently? Check if ticket has messages
            if(data.ticket && data.ticket.messages && Array.isArray(data.ticket.messages)) {
                const lastMessage = data.ticket.messages[data.ticket.messages.length - 1];
                if(lastMessage) {
                    console.log('[Support] supportSocketTicketMessage: Found message in ticket.messages, processing it');
                    // Create a message-like object from ticket data
                    data.message = {
                        ...lastMessage,
                        ticketId: data.ticket._id
                    };
                    // Continue processing with the extracted message
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        
        // Ensure message has ticketId - if not, try to get it from ticket object
        if(!data.message.ticketId) {
            if(data.ticket && data.ticket._id) {
                data.message.ticketId = data.ticket._id;
                console.log('[Support] supportSocketTicketMessage: Added ticketId to message from ticket object');
            } else {
                console.log('[Support] supportSocketTicketMessage: Early return - no ticketId in message or ticket', { 
                    message: data.message,
                    ticket: data.ticket
                });
                return;
            }
        }
        
        // Check if this ticket is currently being viewed (either by user or admin)
        // Check both selectedTicket and ticketDetail.data._id to ensure we catch it
        const detailTicketId = getters.supportTicketDetail.data?._id;
        const selectedTicketId = getters.supportSelectedTicket;
        const messageTicketId = data.message.ticketId;
        
        // Convert to strings for comparison (in case one is ObjectId and other is string)
        const detailTicketIdStr = detailTicketId ? String(detailTicketId) : null;
        const messageTicketIdStr = messageTicketId ? String(messageTicketId) : null;
        const selectedTicketIdStr = selectedTicketId ? String(selectedTicketId) : null;
        
        const isCurrentTicket = selectedTicketIdStr === messageTicketIdStr || detailTicketIdStr === messageTicketIdStr;
        
        console.log('[Support] supportSocketTicketMessage: Ticket check', {
            isCurrentTicket,
            selectedTicket: selectedTicketIdStr,
            detailTicketId: detailTicketIdStr,
            messageTicketId: messageTicketIdStr,
            ticketDetailExists: !!getters.supportTicketDetail.data,
            idsMatch: detailTicketIdStr === messageTicketIdStr
        });
        
        // ALWAYS add message if ticket detail is open and matches the message's ticket
        // This ensures real-time sync between user and admin
        // The most reliable check: if ticketDetail.data exists and its _id matches message's ticketId
        const shouldAddMessage = getters.supportTicketDetail.data && detailTicketIdStr === messageTicketIdStr;
        
        if(shouldAddMessage) {
            console.log('[Support] supportSocketTicketMessage: ✅ Adding message to chat list', {
                ticketId: data.message.ticketId,
                messageId: data.message._id,
                hasMessagesArray: !!getters.supportTicketDetail.messages,
                messagesLength: getters.supportTicketDetail.messages?.length || 0,
                detailTicketId: detailTicketIdStr,
                messageTicketId: messageTicketIdStr
            });
            
            // Check if message already exists to prevent duplicates
            const messageExists = getters.supportTicketDetail.messages?.some(m => m._id === data.message._id);
            if(!messageExists) {
                commit('support_add_ticket_message', data.message);
                console.log('[Support] supportSocketTicketMessage: ✅ Message added successfully');
            } else {
                console.log('[Support] supportSocketTicketMessage: ⚠️ Message already exists, skipping duplicate');
            }
        } else {
            console.log('[Support] supportSocketTicketMessage: ❌ Skipping - ticket detail not open or ID mismatch', {
                ticketDetailExists: !!getters.supportTicketDetail.data,
                detailTicketId: detailTicketIdStr,
                messageTicketId: messageTicketIdStr,
                idsMatch: detailTicketIdStr === messageTicketIdStr,
                selectedTicket: selectedTicketIdStr,
                isCurrentTicket: isCurrentTicket
            });
        }
        
        // Update ticket in the list - ALWAYS update if ticket exists in list
        if(getters.supportTickets.data && Array.isArray(getters.supportTickets.data) && getters.supportTickets.data.length > 0) {
            const ticketIndex = getters.supportTickets.data.findIndex(t => t && t._id === data.message.ticketId);
            if(ticketIndex !== -1) {
                const existingTicket = getters.supportTickets.data[ticketIndex];
                const updatedTicket = { 
                    ...existingTicket, 
                    updatedAt: data.message.createdAt || data.message.updatedAt || new Date().toISOString()
                };
                commit('support_update_ticket', updatedTicket);
                // Sort tickets by updatedAt (most recent first) - this creates new array reference
                commit('support_sort_tickets_by_updated');
            }
        }
        
        // Update ticket detail data if it's the current ticket
        if(isCurrentTicket && getters.supportTicketDetail.data) {
            const updatedTicket = { 
                ...getters.supportTicketDetail.data, 
                updatedAt: data.message.createdAt || data.message.updatedAt || new Date().toISOString()
            };
            commit('support_set_ticket_detail', updatedTicket);
        }
    },
    supportSetFilters({ commit }, filters) {
        commit('support_set_filters', filters);
    }
}

const support = {
    state,
    mutations,
    actions,
    getters
}

export default support;
